import { createSelector } from 'reselect';

import type { LeverKey } from 'Lib/ConfigService/types';
import { createKeySelector } from 'Lib/reduxUtils';
import { getLeverValue } from 'Lib/utils/getLeverValue';
import { pick } from 'Lib/utils/lodashUtils';

import type { EntitiesState, LeverTypes } from './types';

/**
 * Gets all entities from the state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {EntitiesState} entities
 */
export const selectEntities = createKeySelector('entities');

/**
 * Computes entities that have a particular type
 *
 * @param  type - type of entity we're interested in
 * @param  entities - state of the entities
 * @return one type of entities
 */
export const getEntitiesByType = <TEntityType extends keyof EntitiesState>(
  type: TEntityType,
  entities: EntitiesState
): EntitiesState[TEntityType] => entities[type] || {};

/**
 * @returns filtered version of entities only containing the specified entity types.
 */
export const getEntitiesByTypes = (
  types: (keyof EntitiesState)[],
  entities: EntitiesState
): Partial<EntitiesState> => pick(entities, types);

/**
 * @param  types - entity type keys
 * @param  lever - boolean levers only
 * @param  entities - state of the entities
 * @returns a filtered version of entities based on lever value.
 */
export const computeEntitiesByLever = (
  types: LeverTypes,
  lever: LeverKey,
  entities: EntitiesState
) => {
  const isLeverEnabled = getLeverValue(lever);
  return (isLeverEnabled ? entities[types.enabled] : entities[types.disabled]) || {};
};

/**
 * Creates a selector for entities with the specified type from the state
 */
export const createEntitiesSelector = <TEntityType extends keyof EntitiesState>(
  type: TEntityType
) => createSelector([selectEntities], (entities) => getEntitiesByType(type, entities));

/**
 * Creates a selector entities with the specified types from the state
 *
 * @param  types - entity type keys.
 */
export const createEntitiesTypeSelector = (types: (keyof EntitiesState)[]) =>
  createSelector([selectEntities], (entities) => getEntitiesByTypes(types, entities));

/**
 * Creates a selector entities based on lever value from the state.
 */
export const createLeverBasedEntitiesSelector = (types: LeverTypes, lever: LeverKey) =>
  createSelector([selectEntities], (entities) => computeEntitiesByLever(types, lever, entities));
